import React from "react";

export default function LandingPreview({ lm, onSubmit, submitting }) {
  const cfg = lm.landing_config || {};
  if (cfg.layout === "hero-split") {
    return <HeroSplitPreview cfg={cfg} onSubmit={onSubmit} submitting={submitting} />;
  }
  if (cfg.layout === "video-squeeze") {
    return <VideoSqueezePreview cfg={cfg} onSubmit={onSubmit} submitting={submitting} />;
  }
  return <DefaultPreview cfg={cfg} onSubmit={onSubmit} submitting={submitting} />;
}

function DefaultPreview({ cfg, onSubmit, submitting }) {
  const fields = cfg.form_fields || { name_enabled: true, phone_enabled: false };
  const bg = cfg.bg_color || "#FFFFFF";
  const text = cfg.text_color || "#0A0A0A";
  const accent = cfg.accent_color || "#FF4F00";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!onSubmit) return;
    const fd = new FormData(e.target);
    onSubmit({
      name: fd.get("name") || "",
      email: fd.get("email"),
      phone: fd.get("phone") || "",
    });
  };

  return (
    <div style={{ background: bg, color: text }} className="min-h-[600px] font-[DM_Sans]">
      <div className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="inline-block px-3 py-1 border-2 mb-5 text-xs font-bold tracking-[0.2em]" style={{ borderColor: text, background: accent, color: "#fff" }}>
            {cfg.badge || "FREE DOWNLOAD"}
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1] mb-4" style={{ color: text }}>
            {cfg.headline || "Your Headline Here"}
          </h1>
          <p className="text-base md:text-lg opacity-80 mb-5 leading-relaxed">{cfg.subheadline}</p>
          {Array.isArray(cfg.bullets) && cfg.bullets.length > 0 && (
            <ul className="space-y-2 mb-6">
              {cfg.bullets.map((b, i) => (
                <li key={`${i}-${b}`} className="flex items-start gap-2 text-sm">
                  <span className="inline-block w-4 h-4 mt-1" style={{ background: accent }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
          {cfg.hero_video_url ? (
            <HeroVideo url={cfg.hero_video_url} text={text} />
          ) : cfg.image_url ? (
            <img src={cfg.image_url} alt="" className="w-full max-w-md object-cover border-2 mt-2" style={{ borderColor: text }} />
          ) : null}
        </div>

        <div className="border-2 p-6" style={{ borderColor: text, background: "#fff", color: "#0A0A0A", boxShadow: `6px 6px 0 ${text}` }}>
          <div className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: accent }}>Get Instant Access</div>
          <form onSubmit={handleSubmit} className="space-y-3">
            {fields.name_enabled && (
              <div>
                <label className="label-cap block mb-1">Name</label>
                <input name="name" placeholder="Your name" className="w-full px-3 py-3 border-2 outline-none" style={{ borderColor: text }} data-testid="public-name-input" />
              </div>
            )}
            <div>
              <label className="label-cap block mb-1">Email *</label>
              <input name="email" type="email" required placeholder="you@email.com" className="w-full px-3 py-3 border-2 outline-none" style={{ borderColor: text }} data-testid="public-email-input" />
            </div>
            {fields.phone_enabled && (
              <div>
                <label className="label-cap block mb-1">Phone</label>
                <input name="phone" type="tel" placeholder="+1 555 555 5555" className="w-full px-3 py-3 border-2 outline-none" style={{ borderColor: text }} data-testid="public-phone-input" />
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-5 py-4 font-display font-bold border-2 transition-all"
              style={{ borderColor: text, background: accent, color: "#fff", boxShadow: `4px 4px 0 ${text}` }}
              data-testid="public-submit-btn"
            >
              {submitting ? "Sending…" : (cfg.cta_text || "Get Instant Access")}
            </button>
            <p className="text-xs opacity-60 text-center">We respect your privacy. Unsubscribe anytime.</p>
          </form>
        </div>
      </div>
    </div>
  );
}

function HeroVideo({ url, text }) {
  if (!url) return null;
  const isYT = /youtu\.?be/.test(url);
  const isVimeo = /vimeo\.com/.test(url);
  if (isYT) {
    const id = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/)?.[1];
    if (!id) return null;
    return (
      <div className="aspect-video border-2 mt-2 max-w-md" style={{ borderColor: text, boxShadow: `4px 4px 0 ${text}` }}>
        <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${id}`} allowFullScreen title="Video" data-testid="landing-hero-youtube" />
      </div>
    );
  }
  if (isVimeo) {
    const id = url.match(/vimeo\.com\/(\d+)/)?.[1];
    if (!id) return null;
    return (
      <div className="aspect-video border-2 mt-2 max-w-md" style={{ borderColor: text, boxShadow: `4px 4px 0 ${text}` }}>
        <iframe className="w-full h-full" src={`https://player.vimeo.com/video/${id}`} allowFullScreen title="Video" data-testid="landing-hero-vimeo" />
      </div>
    );
  }
  return (
    <video
      src={url}
      controls
      className="w-full max-w-md aspect-video border-2 mt-2"
      style={{ borderColor: text, boxShadow: `4px 4px 0 ${text}` }}
      data-testid="landing-hero-mp4"
    />
  );
}


// ---------------------------------------------------------------------------
// HeroSplitPreview — bold brutalist hero with floating social-proof card + marquee
//
// Creators wrap any word of their headline in {curly braces} to render it with
// the orange highlight box (falls back to the `highlight_word` field if no braces).
// ---------------------------------------------------------------------------
function HeroSplitPreview({ cfg, onSubmit, submitting }) {
  const fields = cfg.form_fields || { name_enabled: true, phone_enabled: false };
  const bg = cfg.bg_color || "#FFFFFF";
  const text = cfg.text_color || "#0A0A0A";
  const accent = cfg.accent_color || "#FF4F00";
  const marquee = Array.isArray(cfg.marquee_words) ? cfg.marquee_words : [];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!onSubmit) return;
    const fd = new FormData(e.target);
    onSubmit({
      name: fd.get("name") || "",
      email: fd.get("email"),
      phone: fd.get("phone") || "",
    });
  };

  return (
    <div style={{ background: bg, color: text }} className="min-h-[600px] font-[DM_Sans]">
      <div className="max-w-6xl mx-auto px-6 py-14 grid lg:grid-cols-2 gap-10 items-start">
        {/* LEFT: badge, headline, subhead, CTA, form */}
        <div>
          <div
            className="inline-flex items-center gap-3 px-4 py-2 border-2 mb-6 text-xs font-bold tracking-[0.2em]"
            style={{ borderColor: text, background: "#fff", color: text }}
          >
            <span className="inline-block w-3 h-3" style={{ background: accent }} />
            <span>{cfg.badge || "VIBE-CODED · BUILT FOR SERIOUS CREATORS"}</span>
          </div>

          <h1
            className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-[0.95] mb-5"
            style={{ color: text }}
            data-testid="hero-split-headline"
          >
            <HighlightedHeadline
              text={cfg.headline || "Your headline here."}
              fallbackWord={cfg.highlight_word}
              accent={accent}
            />
          </h1>

          <p className="text-base md:text-lg opacity-80 mb-8 leading-relaxed max-w-lg">
            {cfg.subheadline}
          </p>

          <form onSubmit={handleSubmit} className="space-y-3 max-w-md">
            {fields.name_enabled && (
              <input
                name="name"
                placeholder="Your name"
                className="w-full px-4 py-4 border-2 outline-none font-medium"
                style={{ borderColor: text, background: "#fff" }}
                data-testid="public-name-input"
              />
            )}
            <input
              name="email"
              type="email"
              required
              placeholder="you@email.com"
              className="w-full px-4 py-4 border-2 outline-none font-medium"
              style={{ borderColor: text, background: "#fff" }}
              data-testid="public-email-input"
            />
            {fields.phone_enabled && (
              <input
                name="phone"
                type="tel"
                placeholder="+1 555 555 5555"
                className="w-full px-4 py-4 border-2 outline-none font-medium"
                style={{ borderColor: text, background: "#fff" }}
                data-testid="public-phone-input"
              />
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full md:w-auto md:min-w-[240px] px-8 py-4 font-display font-bold border-2 transition-all inline-flex items-center justify-center gap-2"
              style={{ borderColor: text, background: accent, color: "#fff", boxShadow: `6px 6px 0 ${text}` }}
              data-testid="public-submit-btn"
            >
              {submitting ? "Sending…" : (cfg.cta_text || "Start Building Free")} <span aria-hidden>→</span>
            </button>
            <p className="text-xs opacity-60">Takes under a minute. No credit card required.</p>
          </form>
        </div>

        {/* RIGHT: hero image with floating social-proof card */}
        <div className="relative mt-4 lg:mt-10">
          {cfg.image_url ? (
            <div
              className="border-2 overflow-hidden"
              style={{ borderColor: text, boxShadow: `10px 10px 0 ${text}` }}
            >
              <img src={cfg.image_url} alt="" className="w-full h-full object-cover aspect-[4/5] md:aspect-[1/1]" />
            </div>
          ) : (
            <div
              className="w-full aspect-square border-2 flex items-center justify-center font-display font-black text-6xl opacity-20"
              style={{ borderColor: text, background: "#f4f4f4" }}
            >
              {(cfg.headline || "VibeLead").slice(0, 2).toUpperCase()}
            </div>
          )}

          {(cfg.stat_value || cfg.stat_caption) && (
            <div
              className="absolute -bottom-6 -left-4 md:left-auto md:right-6 md:-bottom-8 px-5 py-4 border-2 max-w-[260px]"
              style={{ borderColor: text, background: "#fff", color: text, boxShadow: `6px 6px 0 ${text}` }}
              data-testid="hero-split-stat-card"
            >
              {cfg.stat_label && (
                <div className="text-[10px] font-bold tracking-[0.2em] opacity-60 mb-1">
                  {cfg.stat_label}
                </div>
              )}
              {cfg.stat_value && (
                <div className="font-display font-black text-3xl tracking-tighter leading-none">
                  {cfg.stat_value}
                </div>
              )}
              {cfg.stat_caption && (
                <div className="text-xs mt-2 opacity-70">{cfg.stat_caption}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scrolling marquee strip — only renders when words supplied */}
      {marquee.length > 0 && (
        <div
          className="border-t-2 border-b-2 overflow-hidden whitespace-nowrap select-none"
          style={{ borderColor: text, background: text, color: "#fff" }}
          data-testid="hero-split-marquee"
        >
          <div className="inline-flex py-4 animate-[marquee_30s_linear_infinite]">
            {[...marquee, ...marquee, ...marquee].map((word, i) => (
              <span key={i} className="inline-flex items-center gap-6 px-6 font-display font-black text-2xl md:text-3xl tracking-tight">
                <span>{word}</span>
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: accent }} />
              </span>
            ))}
          </div>
          <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-33.3333%); } }`}</style>
        </div>
      )}
    </div>
  );
}

function HighlightedHeadline({ text, fallbackWord, accent }) {
  // Preferred: author wraps the word in {curly braces}.
  const m = text.match(/\{([^}]+)\}/);
  if (m) {
    const [before, after] = text.split(m[0]);
    return (
      <>
        {before}
        <span
          className="inline-block px-3 py-0 align-baseline"
          style={{ background: accent, color: "#fff" }}
        >
          {m[1]}
        </span>
        {after}
      </>
    );
  }
  // Fallback: use the `highlight_word` field if it appears somewhere in the headline.
  if (fallbackWord && text.includes(fallbackWord)) {
    const [before, after] = text.split(fallbackWord);
    return (
      <>
        {before}
        <span
          className="inline-block px-3 py-0 align-baseline"
          style={{ background: accent, color: "#fff" }}
        >
          {fallbackWord}
        </span>
        {after}
      </>
    );
  }
  return <>{text}</>;
}

// ---------------------------------------------------------------------------
// VideoSqueezePreview — classic high-converting video-first layout
//
// Visual traits modeled on the reference sample:
//  - Diagonal corner ribbon top-left ("FREE")
//  - Yellow top tagline (uses `badge` field)
//  - Black italic quoted headline
//  - Centered video embed (YouTube / Vimeo / MP4 via existing HeroVideo)
//  - Yellow CTA button with hand-drawn scribble-circle SVG behind it
//  - Blue underlined link below CTA (`cta_subtext`)
//  - Email-only form below the CTA link (keeps conversion path intact)
// ---------------------------------------------------------------------------
function VideoSqueezePreview({ cfg, onSubmit, submitting }) {
  const bg = cfg.bg_color || "#FFFFFF";
  const text = cfg.text_color || "#0A0A0A";
  const yellow = cfg.accent_color || "#FFD600";
  const scribbleBlue = "#1EA7E0";

  return (
    <div
      style={{ background: bg, color: text }}
      className="min-h-[600px] font-[DM_Sans] relative overflow-hidden"
      data-testid="video-squeeze"
    >
      {cfg.ribbon_text && <CornerRibbon text={cfg.ribbon_text} color={scribbleBlue} />}

      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        {cfg.badge && <TopTagline text={cfg.badge} color={yellow} />}

        <h1
          className="font-display font-black italic text-3xl md:text-5xl tracking-tight leading-[1.1] mb-8"
          style={{ color: text }}
          data-testid="video-squeeze-headline"
        >
          {cfg.headline || "\"Your compelling quoted headline here.\""}
        </h1>

        {cfg.subheadline && (
          <p className="text-base md:text-lg opacity-80 mb-6 leading-relaxed max-w-2xl mx-auto">
            {cfg.subheadline}
          </p>
        )}

        <VideoOrPlaceholder url={cfg.hero_video_url} text={text} />

        <ScribbledCtaForm
          cfg={cfg}
          submitting={submitting}
          onSubmit={onSubmit}
          yellow={yellow}
          text={text}
          scribbleColor={scribbleBlue}
        />

        {cfg.cta_subtext && (
          <div className="mt-6">
            <span
              className="text-base md:text-lg underline cursor-pointer"
              style={{ color: scribbleBlue }}
              data-testid="video-squeeze-subtext"
            >
              {cfg.cta_subtext}
            </span>
          </div>
        )}

        <p className="text-xs opacity-50 mt-6">We respect your privacy. Unsubscribe anytime.</p>
      </div>
    </div>
  );
}

function CornerRibbon({ text, color }) {
  return (
    <div
      className="absolute top-0 left-0 pointer-events-none select-none"
      data-testid="video-squeeze-ribbon"
      style={{ width: 160, height: 160 }}
    >
      <div
        className="absolute font-display font-black text-white text-sm tracking-[0.3em]"
        style={{
          background: color,
          padding: "8px 60px",
          left: "-48px",
          top: "28px",
          transform: "rotate(-45deg)",
          boxShadow: "0 2px 0 rgba(0,0,0,0.15)",
        }}
      >
        {text.toUpperCase()}
      </div>
    </div>
  );
}

function TopTagline({ text, color }) {
  return (
    <div
      className="font-display font-black text-2xl md:text-3xl tracking-tight mb-4 leading-tight"
      style={{ color }}
      data-testid="video-squeeze-badge"
    >
      {text}
    </div>
  );
}

function VideoOrPlaceholder({ url, text }) {
  return (
    <div className="mb-10 flex justify-center">
      {url ? (
        <div className="w-full max-w-2xl">
          <HeroVideo url={url} text={text} />
        </div>
      ) : (
        <div
          className="w-full max-w-2xl aspect-video border-2 flex items-center justify-center font-display font-bold text-xl opacity-30"
          style={{ borderColor: text, background: "#f0f0f0" }}
        >
          Add a Hero Video URL to show the video here
        </div>
      )}
    </div>
  );
}

function ScribbleCircle({ color }) {
  return (
    <svg
      className="absolute pointer-events-none"
      style={{ top: "-18px", left: "-24px", width: "calc(100% + 48px)", height: "calc(100% + 36px)" }}
      viewBox="0 0 400 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d="M 20,50 C 20,20 100,12 200,14 C 310,16 385,24 388,52 C 391,80 300,92 200,90 C 95,88 20,80 20,50 Z"
        fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round" opacity="0.85"
      />
      <path
        d="M 28,55 C 24,24 110,16 200,18 C 305,20 380,30 384,56"
        fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.55"
      />
    </svg>
  );
}

function ScribbledCtaForm({ cfg, submitting, onSubmit, yellow, text, scribbleColor }) {
  const fields = cfg.form_fields || { name_enabled: false, phone_enabled: false };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!onSubmit) return;
    const fd = new FormData(e.target);
    onSubmit({ name: fd.get("name") || "", email: fd.get("email"), phone: fd.get("phone") || "" });
  };
  const inputStyle = { borderColor: text, background: "#fff" };
  const inputClass = "w-full max-w-md px-4 py-3 border-2 outline-none font-medium text-center";

  return (
    <div className="relative inline-block mx-auto mb-4" data-testid="video-squeeze-cta-wrapper">
      <ScribbleCircle color={scribbleColor} />
      <form onSubmit={handleSubmit} className="relative z-10 flex flex-col items-center gap-3">
        {fields.name_enabled && (
          <input name="name" placeholder="Your name" className={inputClass} style={inputStyle} data-testid="public-name-input" />
        )}
        <input
          name="email" type="email" required placeholder="Enter your best email"
          className={inputClass} style={inputStyle} data-testid="public-email-input"
        />
        {fields.phone_enabled && (
          <input name="phone" type="tel" placeholder="+1 555 555 5555" className={inputClass} style={inputStyle} data-testid="public-phone-input" />
        )}
        <button
          type="submit"
          disabled={submitting}
          className="font-display font-black text-base md:text-lg px-8 py-5 border-2 border-dashed transition-all"
          style={{
            background: yellow, color: "#6B5A00", borderColor: "#AD8A00", borderStyle: "dashed",
            boxShadow: "2px 2px 0 rgba(0,0,0,0.1)", textShadow: "0 1px 0 rgba(255,255,255,0.4)",
          }}
          data-testid="public-submit-btn"
        >
          {submitting ? "Sending…" : (cfg.cta_text || "YES! Send It Now! »")}
        </button>
      </form>
    </div>
  );
}

